/**
 * Insure This Vehicle - Insurance Marketplace Component
 * Displays insurance providers with eligibility matching, filters, and quote request
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Progress } from './ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from './ui/accordion';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import {
  Shield,
  ShieldCheck,
  Zap,
  PiggyBank,
  Settings,
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  Star,
  Clock,
  CheckCircle2,
  Building2,
  Phone,
  Mail,
  Globe,
  Filter,
  X,
  Loader2,
  AlertTriangle,
  Car,
  Wrench,
  FileText,
  DollarSign,
  Info,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge configurations
const BADGE_CONFIG = {
  lowest_premium: { label: 'Lowest Premium', icon: PiggyBank, color: 'bg-green-100 text-green-700 border-green-200' },
  fast_cover: { label: 'Fast Cover', icon: Zap, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  best_claims: { label: 'Best Claims', icon: ShieldCheck, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  flexible_cover: { label: 'Flexible Cover', icon: Settings, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  trusted_insurer: { label: 'Trusted Insurer', icon: BadgeCheck, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  good_claims: { label: 'Good Claims', icon: CheckCircle2, color: 'bg-teal-100 text-teal-700 border-teal-200' }
};

// Country-specific disclaimers
const COUNTRY_DISCLAIMERS = {
  SL: "Policy terms may require document verification. Insurance options are provided by third-party insurers regulated by IRCSL.",
  UK: "Insurers shown are authorised and regulated by the FCA. Representative examples shown. Final premium may vary.",
  US: "Insurance requirements and coverage vary by state. Contact insurers directly for state-specific information.",
  AU: "CTP requirements vary by state. Insurers shown are regulated by APRA/ASIC."
};

const InsureThisCar = ({ listing, isDemo = false }) => {
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    premium_level: '',
    claims_rating: '',
    instant_cover: false,
    policy_type: 'comprehensive',
    add_ons: []
  });
  
  // Quote form states
  const [quoteForm, setQuoteForm] = useState({
    buyer_name: '',
    buyer_email: '',
    buyer_phone: '',
    usage_type: 'private',
    policy_type: 'comprehensive',
    add_ons: []
  });

  // Extract vehicle data from listing
  const vehicleData = {
    vehicle_type: listing?.category?.includes('motorcycle') ? 'motorcycle' : 'car',
    vehicle_value: listing?.price || 0,
    vehicle_age: listing?.year ? new Date().getFullYear() - listing.year : 5,
    vehicle_make: listing?.make,
    vehicle_model: listing?.model,
    country: listing?.country || 'SL'
  };

  useEffect(() => {
    fetchCategories();
    fetchProviders();
  }, [listing?.id]);

  useEffect(() => {
    fetchProviders();
  }, [selectedCategory, filters]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/insurance/categories`, {
        params: { country: vehicleData.country }
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const params = {
        country: vehicleData.country,
        vehicle_type: vehicleData.vehicle_type,
        vehicle_value: vehicleData.vehicle_value,
        vehicle_age: vehicleData.vehicle_age,
        ...(selectedCategory && { category: selectedCategory }),
        ...(filters.premium_level && { premium_level: filters.premium_level }),
        ...(filters.claims_rating && { claims_rating: filters.claims_rating }),
        ...(filters.instant_cover && { instant_cover: true }),
        ...(filters.policy_type && { policy_type: filters.policy_type }),
        ...(filters.add_ons.length > 0 && { add_ons: filters.add_ons.join(',') })
      };
      
      const response = await axios.get(`${API_URL}/api/insurance/providers`, { params });
      setProviders(response.data.providers || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
  };

  const handleGetQuote = (provider) => {
    setSelectedProvider(provider);
    setShowQuoteDialog(true);
  };

  const handleSubmitQuote = async () => {
    if (!quoteForm.buyer_name || !quoteForm.buyer_email || !quoteForm.buyer_phone) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      
      await axios.post(`${API_URL}/api/insurance/applications`, {
        listing_id: listing?.id,
        vehicle_type: vehicleData.vehicle_type,
        vehicle_value: vehicleData.vehicle_value,
        vehicle_age: vehicleData.vehicle_age,
        vehicle_make: vehicleData.vehicle_make,
        vehicle_model: vehicleData.vehicle_model,
        usage_type: quoteForm.usage_type,
        location: listing?.location,
        buyer_name: quoteForm.buyer_name,
        buyer_email: quoteForm.buyer_email,
        buyer_phone: quoteForm.buyer_phone,
        policy_type: quoteForm.policy_type,
        add_ons_requested: quoteForm.add_ons,
        provider_id: selectedProvider.id,
        provider_name: selectedProvider.name,
        country: vehicleData.country
      });
      
      setShowQuoteDialog(false);
      alert('Quote request submitted! The insurer will contact you shortly.');
      
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Failed to submit quote request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      premium_level: '',
      claims_rating: '',
      instant_cover: false,
      policy_type: 'comprehensive',
      add_ons: []
    });
    setSelectedCategory(null);
  };

  const formatCurrency = (amount, country) => {
    if (country === 'UK') return `£${amount.toLocaleString()}`;
    if (country === 'US') return `$${amount.toLocaleString()}`;
    return `Rs. ${amount.toLocaleString()}`;
  };

  const getEstimatedPremium = (provider) => {
    // Estimate based on vehicle value and provider's premium level
    const baseRate = provider.premium_level === 'low' ? 0.02 : provider.premium_level === 'high' ? 0.04 : 0.03;
    const annualPremium = vehicleData.vehicle_value * baseRate;
    const monthlyPremium = annualPremium / 12;
    return {
      monthly: Math.round(monthlyPremium),
      annual: Math.round(annualPremium)
    };
  };

  return (
    <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50" data-testid="insure-this-car-section">
      <Accordion type="single" collapsible defaultValue="insurance">
        <AccordionItem value="insurance" className="border-none">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div className="text-left">
                <CardTitle className="text-lg text-indigo-800">Insure this Vehicle</CardTitle>
                <p className="text-sm text-indigo-600">Compare insurance from trusted providers</p>
              </div>
              {isDemo && (
                <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200 ml-2" data-testid="insurance-demo-badge">
                  DEMO
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          
          <AccordionContent className="px-6 pb-6">
            {/* Quick Picks / Category Badges */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => {
                  const config = BADGE_CONFIG[cat.id] || {};
                  const Icon = config.icon || Shield;
                  const isActive = selectedCategory === cat.id;
                  
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryClick(cat.id)}
                      className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
                        transition-all border
                        ${isActive 
                          ? 'bg-indigo-600 text-white border-indigo-600' 
                          : `${config.color || 'bg-gray-100 text-gray-700 border-gray-200'} hover:opacity-80`
                        }
                      `}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {cat.name}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Refine Filters Toggle */}
            <div className="mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="w-full justify-between"
              >
                <span className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Refine Filters
                </span>
                {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
              
              {showFilters && (
                <div className="mt-3 p-4 bg-white rounded-lg border space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Premium Level */}
                    <div>
                      <Label className="text-xs text-gray-500">💰 Premium Level</Label>
                      <Select
                        value={filters.premium_level}
                        onValueChange={(v) => setFilters(prev => ({ ...prev, premium_level: v }))}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Any</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Claims Rating */}
                    <div>
                      <Label className="text-xs text-gray-500">🏦 Claims Rating</Label>
                      <Select
                        value={filters.claims_rating}
                        onValueChange={(v) => setFilters(prev => ({ ...prev, claims_rating: v }))}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Any</SelectItem>
                          <SelectItem value="excellent">Excellent</SelectItem>
                          <SelectItem value="good">Good</SelectItem>
                          <SelectItem value="average">Average</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Speed & Flexibility */}
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={filters.instant_cover}
                        onCheckedChange={(v) => setFilters(prev => ({ ...prev, instant_cover: v }))}
                      />
                      <Zap className="h-4 w-4 text-yellow-500" />
                      Instant Cover
                    </label>
                  </div>
                  
                  {/* Policy Type */}
                  <div>
                    <Label className="text-xs text-gray-500">🔄 Policy Type</Label>
                    <Select
                      value={filters.policy_type}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, policy_type: v }))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="comprehensive">Comprehensive</SelectItem>
                        <SelectItem value="third_party">Third Party Only</SelectItem>
                        <SelectItem value="tpft">Third Party Fire & Theft</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Clear Filters */}
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
                    <X className="h-4 w-4 mr-1" /> Clear Filters
                  </Button>
                </div>
              )}
            </div>
            
            <Separator className="my-4" />
            
            {/* Provider List */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                <span className="ml-2 text-gray-600">Finding best insurance options...</span>
              </div>
            ) : providers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Shield className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No insurance providers available for this vehicle.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {providers.map((provider, index) => {
                  const eligibility = provider.eligibility || {};
                  const premium = getEstimatedPremium(provider);
                  
                  return (
                    <div
                      key={provider.id}
                      className={`
                        p-4 bg-white rounded-lg border-2 transition-all
                        ${provider.is_sponsored ? 'border-yellow-300 bg-yellow-50/30' : 'border-gray-200'}
                        hover:border-indigo-300 hover:shadow-md
                      `}
                    >
                      <div className="flex items-start justify-between gap-4">
                        {/* Provider Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {provider.is_sponsored && (
                              <Badge className="bg-yellow-100 text-yellow-700 text-xs">Sponsored</Badge>
                            )}
                            <h4 className="font-semibold text-gray-900">{provider.name}</h4>
                            {provider.is_verified && (
                              <BadgeCheck className="h-4 w-4 text-emerald-500" />
                            )}
                            {isDemo && !provider.is_sponsored && (
                              <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0.5" data-testid={`insurance-provider-demo-${provider.id}`}>
                                DEMO
                              </Badge>
                            )}
                          </div>
                          
                          {/* Badges */}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {provider.badges?.slice(0, 3).map(badge => {
                              const config = BADGE_CONFIG[badge] || {};
                              const Icon = config.icon || Shield;
                              return (
                                <span
                                  key={badge}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${config.color || 'bg-gray-100 text-gray-600'}`}
                                >
                                  <Icon className="h-3 w-3" />
                                  {config.label || badge}
                                </span>
                              );
                            })}
                          </div>
                          
                          {/* Eligibility Explanation */}
                          {eligibility.explanation && (
                            <p className="text-sm text-gray-600 mb-2">
                              {eligibility.explanation}
                            </p>
                          )}
                          
                          {/* Quick Stats */}
                          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                            {provider.instant_cover && (
                              <span className="flex items-center gap-1">
                                <Zap className="h-3 w-3 text-yellow-500" /> Instant Cover
                              </span>
                            )}
                            {provider.network_garages && (
                              <span className="flex items-center gap-1">
                                <Wrench className="h-3 w-3" /> {provider.garage_count}+ Garages
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {provider.avg_quote_time_minutes}min quote
                            </span>
                          </div>
                        </div>
                        
                        {/* Score & Price */}
                        <div className="text-right">
                          {/* Match Score */}
                          <div className="mb-2">
                            <div className="text-2xl font-bold text-indigo-600">
                              {eligibility.score || 0}%
                            </div>
                            <div className="text-xs text-gray-500">Match Score</div>
                          </div>
                          
                          {/* Estimated Premium */}
                          <div className="mb-3">
                            <div className="text-lg font-semibold text-gray-900">
                              {formatCurrency(premium.monthly, vehicleData.country)}/mo
                            </div>
                            <div className="text-xs text-gray-500">Est. premium</div>
                          </div>
                          
                          <Button
                            size="sm"
                            onClick={() => handleGetQuote(provider)}
                            className="bg-indigo-600 hover:bg-indigo-700"
                          >
                            Get Quote
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Legal Disclaimer */}
            <Alert className="mt-4 bg-gray-50 border-gray-200">
              <Info className="h-4 w-4 text-gray-500" />
              <AlertDescription className="text-xs text-gray-600">
                {COUNTRY_DISCLAIMERS[vehicleData.country] || COUNTRY_DISCLAIMERS.SL}
                <br />
                <span className="font-medium">
                  Insurance options are provided by third-party insurers. Premiums and cover are subject to insurer approval. Zeilian does not provide insurance advice.
                </span>
              </AlertDescription>
            </Alert>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      
      {/* Quote Request Dialog */}
      <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-indigo-600" />
              Get Insurance Quote
            </DialogTitle>
            <DialogDescription>
              Request a quote from {selectedProvider?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Vehicle Summary */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Car className="h-8 w-8 text-gray-400" />
                <div>
                  <p className="font-medium">
                    {listing?.year} {listing?.make} {listing?.model}
                  </p>
                  <p className="text-sm text-gray-500">
                    Value: {formatCurrency(vehicleData.vehicle_value, vehicleData.country)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label htmlFor="buyer_name">Full Name *</Label>
                <Input
                  id="buyer_name"
                  placeholder="Your full name"
                  value={quoteForm.buyer_name}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, buyer_name: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="buyer_email">Email *</Label>
                <Input
                  id="buyer_email"
                  type="email"
                  placeholder="your@email.com"
                  value={quoteForm.buyer_email}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, buyer_email: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="buyer_phone">Phone *</Label>
                <Input
                  id="buyer_phone"
                  type="tel"
                  placeholder="+94 77 123 4567"
                  value={quoteForm.buyer_phone}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, buyer_phone: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Coverage Options */}
            <div>
              <Label>Usage Type</Label>
              <Select
                value={quoteForm.usage_type}
                onValueChange={(v) => setQuoteForm(prev => ({ ...prev, usage_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private Use</SelectItem>
                  <SelectItem value="business">Business Use</SelectItem>
                  <SelectItem value="taxi">Taxi / Hire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Policy Type</Label>
              <Select
                value={quoteForm.policy_type}
                onValueChange={(v) => setQuoteForm(prev => ({ ...prev, policy_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="comprehensive">Comprehensive</SelectItem>
                  <SelectItem value="third_party">Third Party Only</SelectItem>
                  <SelectItem value="tpft">Third Party Fire & Theft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuoteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitQuote}
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Request Quote'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InsureThisCar;
